import {
  Box,
  Button,
  Card,
  Col,
  ConfigProvider,
  Flex,
  Form,
  type OperationType,
  RadioGroupField,
  Row,
  SelectField,
  SubmitButton,
  Text,
  TextInputField,
} from '@/components';
import { UserSelectField } from '@/features/tenant-manager/components';
import { useHierarchyLabels } from '@/features/tenant-manager/hooks';
import { useWorkflowStackSiderView } from '@/features/workflow/hooks/stack-sider-view';
import {
  useBulkUpsertAndDeleteStages,
  useWorkflow,
} from '@/features/workflow/hooks/workflow';
import {
  WorkflowStageFormSchema,
  WorkflowStageTypeLabelModifier,
  type WorkflowStagesModel,
  WorkflowTypeLabelModifier,
  workflowStageType,
  workflowStatusType,
  workflowType,
} from '@/features/workflow/models/workflow';
import { useForm } from '@/hooks';
import { useMessage } from '@/providers';
import { isRS } from '@/utils';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, Fragment } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';

const workflowTypeOptions = workflowType.options.map((value) => ({
  value,
  label: WorkflowTypeLabelModifier[value],
}));

const workflowStageTypeOptions = workflowStageType.options.map((value) => ({
  value,
  label: WorkflowStageTypeLabelModifier[value],
}));

type FormValues = z.infer<typeof WorkflowStageFormSchema>;

type Props = {
  type: OperationType;
};

export const StageForm: FC<Props> = ({ type }) => {
  const { t } = useTranslation();
  const stackSiderView = useWorkflowStackSiderView();

  const siderConfig = stackSiderView.lastItem?.config;

  invariant(
    siderConfig?.viewType === 'workflow-details',
    t('unreachable-code'),
  );

  const { openMessage } = useMessage();
  const { data: workflow } = useWorkflow(siderConfig.id);
  const { mutateAsync: updateWorkflowStages } = useBulkUpsertAndDeleteStages();

  return (
    <InnerForm
      {...(type === 'new' &&
        isRS() && {
          initialValues: {
            workflowType: 'strict',
          },
        })}
      {...(type === 'edit' && {
        initialValues: {
          stages: workflow?.stages.map((stage) => ({
            id: stage.id,
            stageType: stage.stageType,
            approverIds: stage.approvals?.map(
              (approval) => approval.approver.id,
            ),
            title: (stage.title ?? null) as any,
            labelId: stage.label?.id,
          })),
          workflowType: workflow?.workflowType,
        },
      })}
      workflowStatus={workflow?.status}
      stages={workflow?.stages}
      onSubmit={async (values) => {
        await updateWorkflowStages({
          ...values,
          id: siderConfig.id,
          stages: values.stages?.length
            ? values.stages?.map((v) => ({
                ...v,
                labelId: v.labelId ?? null,
              }))
            : null,
        });

        openMessage('success', t('message-create-completed'));
        stackSiderView.pop();
      }}
    />
  );
};

const InnerForm: FC<{
  initialValues?: Partial<FormValues>;
  workflowStatus?: z.infer<typeof workflowStatusType>;
  stages?: z.infer<typeof WorkflowStagesModel>;
  onSubmit: (values: FormValues) => void;
}> = ({ initialValues, workflowStatus, stages, onSubmit }) => {
  const { t } = useTranslation();

  const { data: hierarchyLabelOptions } = useHierarchyLabels({
    params: { enabledOnly: true },
    select: (data) =>
      data.map((label) => ({
        value: label.id,
        label: label.name,
      })),
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(WorkflowStageFormSchema),
  });

  const {
    fields: stageFields,
    append: appendStage,
    insert: insertStage,
    remove: removeStage,
  } = useFieldArray({
    control,
    name: 'stages',
    keyName: 'fieldId',
  });

  return (
    <ConfigProvider theme={{ components: { Form: { itemMarginBottom: 0 } } }}>
      <Form
        leavePageAlertCondition={{
          isDirty,
          isSubmitting,
          isSubmitSuccessful,
        }}
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
      >
        <Flex vertical gap={32}>
          <Flex vertical gap="middle">
            <Text text={t('workflow')} variant="h5" />

            <RadioGroupField
              name="workflowType"
              label={t('workflow-type')}
              control={control}
              options={workflowTypeOptions}
              errors={errors}
              optionsDirection="vertical"
              disabled={workflowStatus !== workflowStatusType.Values.pending}
              required
            />
          </Flex>

          <Flex vertical gap="middle">
            <Text text={t('stage')} variant="h5" />

            <Form.Item>
              <Row gutter={[0, 16]}>
                {stageFields.map((item, idx) => {
                  const currentStage = stages?.find(
                    (stage) => stage.id === item.id,
                  );

                  const canEditStageWithoutApprover =
                    !item.id || currentStage?.status === 'pending';
                  const canEditApprover =
                    (item.id && currentStage?.status === 'rejected') || false;
                  const canDeleteStage =
                    !item.id || currentStage?.status === 'pending';

                  return (
                    <Fragment key={item.fieldId}>
                      {idx !== 0 && (
                        <Col span={24}>
                          <Button
                            type="dashed"
                            onClick={() => {
                              insertStage(idx, {
                                title: null as any,
                                stageType: null as any,
                                approverIds: [],
                                labelId: undefined,
                              });
                            }}
                            block
                            icon={<PlusOutlined aria-label="" />}
                          >
                            {t('add')}
                          </Button>
                        </Col>
                      )}
                      <Col span={24}>
                        <Card size="small">
                          <Flex gap={16} vertical>
                            <Flex justify="flex-end">
                              {canDeleteStage && (
                                <Button
                                  type="link"
                                  size="small"
                                  onClick={() => {
                                    removeStage(idx);
                                  }}
                                >
                                  <CloseOutlined />
                                </Button>
                              )}
                            </Flex>
                            <TextInputField
                              name={`stages.${idx}.title`}
                              autocompleteKey="stage.title"
                              label={t('workflow.stage-title-form-label')}
                              placeholder={t('placeholder-stage-title')}
                              control={control}
                              errors={errors}
                              disabled={!canEditStageWithoutApprover}
                            />

                            <SelectField
                              name={`stages.${idx}.labelId`}
                              label={t('label')}
                              control={control}
                              options={hierarchyLabelOptions}
                              errors={errors}
                              useNotInOption
                              labelRender={({ value }) => {
                                const matchHierarchyLabel =
                                  hierarchyLabelOptions?.find(
                                    (label) => label.value === value,
                                  );
                                return matchHierarchyLabel?.label ?? value;
                              }}
                              disabled={!canEditStageWithoutApprover}
                            />

                            <RadioGroupField
                              name={`stages.${idx}.stageType`}
                              label={t('stage-type')}
                              control={control}
                              options={workflowStageTypeOptions}
                              errors={errors}
                              disabled={!canEditStageWithoutApprover}
                              required
                            />
                            <UserSelectField
                              mode="multiple"
                              name={`stages.${idx}.approverIds`}
                              label={t('approver')}
                              control={control}
                              errors={errors}
                              organizationFilter="modal"
                              useNotInOption
                              disabled={canEditApprover}
                            />
                          </Flex>
                        </Card>
                      </Col>
                    </Fragment>
                  );
                })}

                <Col span={24}>
                  <Button
                    type="dashed"
                    onClick={() => {
                      appendStage({
                        title: null as any,
                        stageType: null as any,
                        approverIds: [],
                        labelId: undefined,
                      });
                    }}
                    block
                    icon={<PlusOutlined aria-label="" />}
                  >
                    {t('add')}
                  </Button>
                </Col>
              </Row>
            </Form.Item>

            <Box>
              <SubmitButton
                loading={isSubmitting}
                disabled={!isDirty || stageFields.length === 0}
              />
            </Box>
          </Flex>
        </Flex>
      </Form>
    </ConfigProvider>
  );
};
